'use client';

import { PriceData, Product } from '@/models/Product';
import { useCallback, useEffect, useRef, useState } from 'react';
import OptimizedProductCard from './OptimizedProductCard';
import DotLoader, { DotEllipsis } from './ui/DotLoader';

interface VirtualizedProductListProps {
  products?: Product[];
  title: string;
  url: string;
  slug?: string;
  showMore: boolean;
  showTitle: boolean;
  priceData: PriceData | null;
  itemsPerPage?: number;
}

export default function VirtualizedProductList({
  products = [],
  title,
  url,
  slug,
  showMore,
  showTitle,
  priceData,
  itemsPerPage = 12,
}: VirtualizedProductListProps) {
  const [visibleItems, setVisibleItems] = useState<number>(itemsPerPage);
  const [isLoading, setIsLoading] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const sectionId = `product-section-${slug || title.replace(/\s+/g, '-').toLowerCase()}`;

  const loadMore = useCallback(() => {
    if (isLoading || visibleItems >= products.length) return;

    setIsLoading(true);
    setTimeout(() => {
      setVisibleItems((prev) => Math.min(prev + itemsPerPage, products.length));
      setIsLoading(false);
    }, 100);
  }, [isLoading, visibleItems, products.length, itemsPerPage]);

  useEffect(() => {
    if (!loadMoreRef.current) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && visibleItems < products.length) {
          loadMore();
        }
      },
      { threshold: 0.1 },
    );

    observerRef.current.observe(loadMoreRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loadMore, visibleItems, products.length]);

  const visibleProducts = products.slice(0, visibleItems);

  return (
    <section className="virtualized-product-list" aria-labelledby={sectionId}>
      <div className="px-4 md:px-0 flex justify-between items-center">
        {showTitle ? (
          <h2
            id={sectionId}
            className="text-hzc_purple_500 font-bold text-xl sm:text-2xl md:text-3xl xl:text-4xl 2xl:text-[44px]"
          >
            {title}
          </h2>
        ) : (
          <div />
        )}
      </div>

      <div className="virtualized-product-grid">
        {visibleProducts.map((product, index) => (
          <div key={product.id} className="product-grid-item">
            <OptimizedProductCard
              product={product}
              priority={index < 6} // Only prioritize first 6 items
              price={priceData?.[product.id]}
              listId={slug || ''}
              listName={title}
              className="mb-4" // Add consistent margin bottom
            />
          </div>
        ))}
      </div>

      {/* Load more trigger */}
      {visibleItems < products.length && (
        <div
          ref={loadMoreRef}
          className="flex justify-center py-4"
          aria-label="Daha fazla ürün yükle"
        >
          {isLoading ? <DotLoader className="text-hzc_purple_500" /> : <div className="h-4" />}
        </div>
      )}

      {visibleItems >= products.length && (
        <div className="py-6 text-center">
          <DotEllipsis className="text-2xl md:text-3xl text-neutral-400" />
        </div>
      )}
    </section>
  );
}
